import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen viewer for the report's photos, with a zoom widget.
 *
 * Zoom: wheel or trackpad pinch (around the cursor), two-finger pinch,
 * double-click / double-tap, the − / + buttons, or + − 0 on the keyboard.
 * Once zoomed, drag to move around, or drag the frame on the mini-map. The
 * original photo is only fetched when the reader zooms in; the page copy
 * (1600 px) is what opens first, and is already in the browser cache.
 *
 * The photo grows out of its thumbnail when it opens — the page's one
 * animation, skipped when the reader asks for reduced motion.
 */

const MAX_SCALE = 8;
const ZOOM_STEP = 1.5;
const SWIPE_DISTANCE = 60;
const DOUBLE_TAP_MS = 300;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export default function Lightbox({ figures, index, onIndex, onClose, originRect }) {
  const { t } = useTranslation();
  const figure = figures[index];
  const total = figures.length;

  const dialogRef = useRef(null);
  const stageRef = useRef(null);
  const closeRef = useRef(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState(null); // { w, h } of the shown photo
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [src, setSrc] = useState(figure.src);
  const [entering, setEntering] = useState(Boolean(originRect) && !prefersReducedMotion());
  const [animate, setAnimate] = useState(false);
  const viewRef = useRef(view);
  viewRef.current = view;

  // ---- geometry ----
  const fit = natural && stage.w && stage.h
    ? Math.min(stage.w / natural.w, stage.h / natural.h, 2)
    : 0;
  const base = { w: natural ? natural.w * fit : 0, h: natural ? natural.h * fit : 0 };

  const clampView = useCallback((v) => {
    const s = clamp(v.s, 1, MAX_SCALE);
    const maxX = Math.max(0, (base.w * s - stage.w) / 2);
    const maxY = Math.max(0, (base.h * s - stage.h) / 2);
    return { s, x: clamp(v.x, -maxX, maxX), y: clamp(v.y, -maxY, maxY) };
  }, [base.w, base.h, stage.w, stage.h]);

  /** Zoom to `next`, keeping the point (cx, cy) — relative to the stage centre — still. */
  const zoomTo = useCallback((next, cx = 0, cy = 0) => {
    setAnimate(false);
    setView((v) => {
      const s = clamp(next, 1, MAX_SCALE);
      const k = s / v.s;
      return clampView({ s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k });
    });
  }, [clampView]);

  const reset = useCallback(() => {
    setAnimate(!prefersReducedMotion());
    setView({ s: 1, x: 0, y: 0 });
  }, []);

  // ---- stage size ----
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep the view valid when the window changes size.
  useEffect(() => { setView((v) => clampView(v)); }, [clampView]);

  // ---- a new photo: reset, and warm up its neighbours ----
  useEffect(() => {
    setNatural(null);
    setSrc(figure.src);
    setView({ s: 1, x: 0, y: 0 });
    [figures[index - 1], figures[index + 1]].forEach((f) => {
      if (f) { const img = new Image(); img.src = f.src; }
    });
  }, [figure.src, figures, index]);

  // ---- the original, only once the reader zooms in ----
  useEffect(() => {
    if (view.s <= 1.3 || src === figure.full_src) return undefined;
    let alive = true;
    const img = new Image();
    img.onload = () => { if (alive) setSrc(figure.full_src); };
    img.src = figure.full_src;
    return () => { alive = false; };
  }, [view.s, src, figure.full_src]);

  // ---- opening: grow out of the thumbnail ----
  useEffect(() => {
    if (!entering || !natural || !base.w) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
      setAnimate(true);
      setEntering(false);
    }));
    return () => cancelAnimationFrame(raf);
  }, [entering, natural, base.w]);

  // ---- focus, keyboard, scroll lock ----
  useEffect(() => {
    const opener = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (opener && opener.focus) opener.focus();
    };
  }, []);

  const go = useCallback((delta) => {
    const next = index + delta;
    if (next >= 0 && next < total) onIndex(next);
  }, [index, total, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowRight' && viewRef.current.s === 1) { e.preventDefault(); go(1); return; }
      if (e.key === 'ArrowLeft' && viewRef.current.s === 1) { e.preventDefault(); go(-1); return; }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(viewRef.current.s * ZOOM_STEP); return; }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTo(viewRef.current.s / ZOOM_STEP); return; }
      if (e.key === '0') { e.preventDefault(); reset(); return; }
      if (e.key === 'Tab' && dialogRef.current) {
        const items = dialogRef.current.querySelectorAll('button:not([disabled])');
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose, reset, zoomTo]);

  // ---- wheel / trackpad pinch (non-passive, to keep the page from scrolling) ----
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const cx = e.clientX - r.left - r.width / 2;
      const cy = e.clientY - r.top - r.height / 2;
      const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002));
      zoomTo(viewRef.current.s * factor, cx, cy);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  // ---- pointers: pan, pinch, swipe, double-tap ----
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef({ t: 0, x: 0, y: 0 });

  const local = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 };
  };

  const onPointerDown = (e) => {
    if (e.target.closest && e.target.closest('button')) return;
    if (e.button !== undefined && e.button !== 0) return;
    stageRef.current.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    setAnimate(false);
    const pts = [...pointers.current.values()];
    const v = viewRef.current;
    if (pts.length === 2) {
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      gesture.current = {
        kind: 'pinch', dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1, mid, v,
      };
    } else if (pts.length === 1) {
      gesture.current = { kind: v.s > 1 ? 'pan' : 'swipe', start: pts[0], v, moved: false };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, local(e));
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.kind === 'pinch' && pts.length === 2) {
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const s = clamp(g.v.s * (dist / g.dist), 1, MAX_SCALE);
      const k = s / g.v.s;
      setView(clampView({ s, x: mid.x - (g.mid.x - g.v.x) * k, y: mid.y - (g.mid.y - g.v.y) * k }));
      return;
    }
    const p = pts[0];
    const dx = p.x - g.start.x;
    const dy = p.y - g.start.y;
    if (Math.hypot(dx, dy) > 6) g.moved = true;
    if (g.kind === 'pan') setView(clampView({ s: g.v.s, x: g.v.x + dx, y: g.v.y + dy }));
    if (g.kind === 'swipe' && g.moved) setView({ s: 1, x: dx, y: 0 });
  };

  const onPointerUp = (e) => {
    const p = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (!g || !p) return;
    if (g.kind === 'pinch') {
      // One finger left: carry on as a pan from here.
      const rest = [...pointers.current.values()][0];
      gesture.current = rest ? { kind: 'pan', start: rest, v: viewRef.current, moved: true } : null;
      return;
    }
    gesture.current = null;
    if (g.kind === 'swipe' && g.moved) {
      const dx = p.x - g.start.x;
      if (dx <= -SWIPE_DISTANCE && index < total - 1) { go(1); return; }
      if (dx >= SWIPE_DISTANCE && index > 0) { go(-1); return; }
      reset();
      return;
    }
    if (!g.moved) {
      const now = Date.now();
      const last = lastTap.current;
      if (now - last.t < DOUBLE_TAP_MS && Math.hypot(p.x - last.x, p.y - last.y) < 30) {
        lastTap.current = { t: 0, x: 0, y: 0 };
        setAnimate(!prefersReducedMotion());
        if (viewRef.current.s > 1) setView({ s: 1, x: 0, y: 0 });
        else setView((v) => {
          const s = 2.5;
          return clampView({ s, x: p.x - (p.x - v.x) * s, y: p.y - (p.y - v.y) * s });
        });
      } else {
        lastTap.current = { t: now, x: p.x, y: p.y };
      }
    }
  };

  // ---- mini-map: where the zoomed view is, and a way to move it ----
  const MINI_W = 148;
  const mini = natural ? { w: MINI_W, h: Math.round(MINI_W * (natural.h / natural.w)) } : null;
  const shown = { w: base.w * view.s, h: base.h * view.s };
  const frame = mini && shown.w ? {
    left: clamp(((shown.w / 2 - view.x) - stage.w / 2) / shown.w, 0, 1),
    top: clamp(((shown.h / 2 - view.y) - stage.h / 2) / shown.h, 0, 1),
    width: clamp(stage.w / shown.w, 0, 1),
    height: clamp(stage.h / shown.h, 0, 1),
  } : null;

  const panFromMini = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const fx = clamp((e.clientX - r.left) / r.width, 0, 1);
    const fy = clamp((e.clientY - r.top) / r.height, 0, 1);
    setAnimate(false);
    setView((v) => {
      const w = base.w * v.s;
      const h = base.h * v.s;
      return clampView({ s: v.s, x: w / 2 - fx * w, y: h / 2 - fy * h });
    });
  };

  // ---- transform ----
  let transform = `translate(-50%, -50%) translate(${view.x}px, ${view.y}px) scale(${view.s})`;
  if (entering && originRect && base.w && stageRef.current) {
    // Start where the thumbnail is, at its size.
    const r = stageRef.current.getBoundingClientRect();
    const sx = originRect.left + originRect.width / 2 - (r.left + r.width / 2);
    const sy = originRect.top + originRect.height / 2 - (r.top + r.height / 2);
    const k = Math.min(originRect.width / base.w, originRect.height / base.h);
    transform = `translate(-50%, -50%) translate(${sx}px, ${sy}px) scale(${k})`;
  }

  // Magnification of the fitted view: 100 % is the whole photo on screen. (A
  // pixel ratio would read 64 % at fit and jump when the original loads.)
  const percent = Math.round(view.s * 100);

  return (
    <div
      ref={dialogRef}
      className={`p-lightbox ${entering ? 'is-entering' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('portal.viewerLabel', { n: figure.number })}
    >
      <div className="p-lb-top">
        <p className="p-lb-count" aria-live="polite">
          {t('portal.photoN', { n: figure.number })}
          <span className="p-lb-of">{t('portal.ofTotal', { index: index + 1, total })}</span>
        </p>
        <button
          ref={closeRef}
          type="button"
          className="p-lb-btn p-lb-close"
          onClick={onClose}
          aria-label={t('portal.close')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>

      <div
        ref={stageRef}
        className={`p-lb-stage ${view.s > 1 ? 'is-zoomed' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          key={figure.id}
          className={`p-lb-img ${animate ? 'is-animated' : ''}`}
          src={src}
          alt={figure.caption || t('portal.photoN', { n: figure.number })}
          draggable={false}
          onLoad={(e) => {
            if (!natural) setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
          }}
          style={{
            width: base.w || undefined,
            height: base.h || undefined,
            visibility: base.w ? 'visible' : 'hidden',
            transform,
          }}
        />

        {index > 0 && (
          <button type="button" className="p-lb-btn p-lb-nav p-lb-prev" onClick={() => go(-1)}
            aria-label={t('portal.previous')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
        )}
        {index < total - 1 && (
          <button type="button" className="p-lb-btn p-lb-nav p-lb-next" onClick={() => go(1)}
            aria-label={t('portal.next')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        )}

        {mini && view.s > 1 && frame && (
          <div
            className="p-lb-mini"
            style={{ width: mini.w, height: mini.h }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.currentTarget.setPointerCapture?.(e.pointerId);
              panFromMini(e);
            }}
            onPointerMove={(e) => { e.stopPropagation(); if (e.buttons) panFromMini(e); }}
            onPointerUp={(e) => e.stopPropagation()}
            aria-hidden="true"
          >
            <img src={figure.src} alt="" draggable={false} />
            <span
              className="p-lb-mini-frame"
              style={{
                left: `${frame.left * 100}%`,
                top: `${frame.top * 100}%`,
                width: `${frame.width * 100}%`,
                height: `${frame.height * 100}%`,
              }}
            />
          </div>
        )}
      </div>

      <div className="p-lb-bottom">
        {figure.caption ? <p className="p-lb-caption">{figure.caption}</p> : <span />}
        <div className="p-lb-zoom" role="group" aria-label={t('portal.zoom')}>
          <button type="button" className="p-lb-btn" onClick={() => zoomTo(view.s / ZOOM_STEP)}
            disabled={view.s <= 1} aria-label={t('portal.zoomOut')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12" /></svg>
          </button>
          <output className="p-lb-percent" aria-live="polite">{percent}&nbsp;%</output>
          <button type="button" className="p-lb-btn" onClick={() => zoomTo(view.s * ZOOM_STEP)}
            disabled={view.s >= MAX_SCALE} aria-label={t('portal.zoomIn')}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h12M12 6v12" /></svg>
          </button>
          <button type="button" className="p-lb-btn p-lb-fit" onClick={reset} disabled={view.s === 1}>
            {t('portal.fit')}
          </button>
        </div>
      </div>
    </div>
  );
}
